import type { FlowDefinition, PageFlowState } from '../types';

export interface PublicSiteProgressStep {
  id: string;
  label: string;
}

export type StepTrackerStatusMode = 'current' | 'completed';

export const STEP_TRACKER_ACTIVE_STEP_FIELD_ID = 'stepTrackerActiveStep';
export const STEP_TRACKER_ACTION_REVISION_FIELD_ID = 'stepTrackerActionRevision';
export const DEFAULT_STEP_TRACKER_ID = 'main-step-tracker';

export const DEFAULT_PUBLIC_SITE_PROGRESS_STEPS: PublicSiteProgressStep[] = [
  { id: 'design', label: 'Choose Design' },
  { id: 'finish', label: 'Select Finish' },
  { id: 'colours', label: 'Pick Colours' },
  { id: 'combinations', label: 'Browse Combinations' },
  { id: 'order', label: 'Place Order' },
];

function normalizeString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
}

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => {
      if (!entry || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
}

export function resolveProgressSteps(value: unknown): PublicSiteProgressStep[] {
  if (!Array.isArray(value)) {
    return DEFAULT_PUBLIC_SITE_PROGRESS_STEPS.map((step) => ({ ...step }));
  }

  const steps = value
    .map((entry, index): PublicSiteProgressStep | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as Record<string, unknown>;
      const fallback = DEFAULT_PUBLIC_SITE_PROGRESS_STEPS[index] ?? {
        id: `step-${index + 1}`,
        label: `Step ${index + 1}`,
      };
      const id = normalizeString(raw.id, fallback.id);
      const label = normalizeString(raw.label, fallback.label);
      if (!id || !label) return null;
      return { id, label };
    })
    .filter((entry): entry is PublicSiteProgressStep => Boolean(entry));

  if (!steps.length) {
    return DEFAULT_PUBLIC_SITE_PROGRESS_STEPS.map((step) => ({ ...step }));
  }

  const seenIds = new Set<string>();
  return steps.filter((step) => {
    if (seenIds.has(step.id)) return false;
    seenIds.add(step.id);
    return true;
  });
}

export function resolveStepTrackerStickySectionIds(value: unknown): string[] {
  return normalizeStringList(value);
}

export function resolveStepTrackerContent(value: unknown, fallbackTrackerId?: string): {
  trackerId: string;
  trackerName: string;
  steps: PublicSiteProgressStep[];
  stickySectionIds: string[];
  defaultStepId: string;
  statusMode: StepTrackerStatusMode;
} {
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
  const steps = resolveProgressSteps(raw.steps);
  const fallbackStepId = steps[0]?.id ?? DEFAULT_PUBLIC_SITE_PROGRESS_STEPS[0]?.id ?? '';
  const defaultStepId = normalizeString(raw.defaultStepId, fallbackStepId);
  const trackerId = normalizeString(raw.trackerId, fallbackTrackerId ?? DEFAULT_STEP_TRACKER_ID);
  const trackerName = normalizeString(raw.trackerName, 'Step Tracker');
  const statusMode: StepTrackerStatusMode = raw.statusMode === 'current' ? 'current' : 'completed';
  return {
    trackerId,
    trackerName,
    steps,
    stickySectionIds: resolveStepTrackerStickySectionIds(raw.stickySectionIds),
    defaultStepId,
    statusMode,
  };
}

function getStepTrackerActiveStepFieldId(trackerId: string | null | undefined) {
  const normalizedTrackerId = typeof trackerId === 'string' ? trackerId.trim() : '';
  return normalizedTrackerId
    ? `${STEP_TRACKER_ACTIVE_STEP_FIELD_ID}:${normalizedTrackerId}`
    : STEP_TRACKER_ACTIVE_STEP_FIELD_ID;
}

function getStepTrackerActionRevisionFieldId(trackerId: string | null | undefined) {
  const normalizedTrackerId = typeof trackerId === 'string' ? trackerId.trim() : '';
  return normalizedTrackerId
    ? `${STEP_TRACKER_ACTION_REVISION_FIELD_ID}:${normalizedTrackerId}`
    : STEP_TRACKER_ACTION_REVISION_FIELD_ID;
}

export function isStepTrackerRuntimeFieldId(fieldId: string) {
  return (
    fieldId === STEP_TRACKER_ACTIVE_STEP_FIELD_ID ||
    fieldId.startsWith(`${STEP_TRACKER_ACTIVE_STEP_FIELD_ID}:`) ||
    fieldId === STEP_TRACKER_ACTION_REVISION_FIELD_ID ||
    fieldId.startsWith(`${STEP_TRACKER_ACTION_REVISION_FIELD_ID}:`)
  );
}

export function stripStepTrackerRuntimeState(flowState: PageFlowState): PageFlowState {
  return Object.fromEntries(
    Object.entries(flowState).map(([flowId, entry]) => [
      flowId,
      {
        ...entry,
        values: Object.fromEntries(
          Object.entries(entry.values ?? {}).filter(([fieldId]) => !isStepTrackerRuntimeFieldId(fieldId))
        ),
      },
    ])
  );
}

export function resolveStepTrackerActiveStep(
  flowState: PageFlowState | null | undefined,
  flows: FlowDefinition[] | null | undefined,
  trackerId?: string | null
): { stepId: string | null; hasAction: boolean } {
  const flowList = Array.isArray(flows) ? flows : [];
  let latestValue = '';
  let latestUpdatedAt = '';
  const targetedFieldId = getStepTrackerActiveStepFieldId(trackerId);
  const targetedActionFieldId = getStepTrackerActionRevisionFieldId(trackerId);

  flowList.forEach((flow) => {
    const entry = flowState?.[flow.id];
    const hasAction =
      entry?.values?.[targetedActionFieldId] != null ||
      (targetedActionFieldId !== STEP_TRACKER_ACTION_REVISION_FIELD_ID
        ? entry?.values?.[STEP_TRACKER_ACTION_REVISION_FIELD_ID] != null
        : false);
    if (!hasAction) return;

    const value =
      entry?.values?.[targetedFieldId] ??
      (targetedFieldId !== STEP_TRACKER_ACTIVE_STEP_FIELD_ID
        ? entry?.values?.[STEP_TRACKER_ACTIVE_STEP_FIELD_ID]
        : undefined);
    if (typeof value !== 'string' || !value.trim()) return;
    const updatedAt = typeof entry?.updatedAt === 'string' ? entry.updatedAt : '';
    if (!latestValue || updatedAt >= latestUpdatedAt) {
      latestValue = value.trim();
      latestUpdatedAt = updatedAt;
    }
  });

  return {
    stepId: latestValue || null,
    hasAction: Boolean(latestValue),
  };
}

export function resolveStepTrackerActiveStepId(
  flowState: PageFlowState | null | undefined,
  flows: FlowDefinition[] | null | undefined,
  trackerId?: string | null
) {
  return resolveStepTrackerActiveStep(flowState, flows, trackerId).stepId;
}

export function setStepTrackerActiveStepInState(
  state: PageFlowState,
  flows: FlowDefinition[],
  stepId: string,
  trackerId?: string | null
): PageFlowState {
  const normalizedStepId = stepId.trim();
  if (!normalizedStepId) return state;
  const fieldId = getStepTrackerActiveStepFieldId(trackerId);
  const actionRevisionFieldId = getStepTrackerActionRevisionFieldId(trackerId);
  const updatedAt = new Date().toISOString();
  return flows.reduce<PageFlowState>((nextState, flow) => {
    const currentEntry = nextState[flow.id] ?? { values: {}, updatedAt };
    if (currentEntry.values?.[fieldId] === normalizedStepId && currentEntry.values?.[actionRevisionFieldId]) {
      return {
        ...nextState,
        [flow.id]: {
          ...currentEntry,
          updatedAt,
        },
      };
    }
    return {
      ...nextState,
      [flow.id]: {
        values: {
          ...currentEntry.values,
          [fieldId]: normalizedStepId,
          [actionRevisionFieldId]: updatedAt,
        },
        updatedAt,
      },
    };
  }, state);
}

export function layoutHasSiteProgressBlock(
  layout: { sections?: Array<{ settings?: { canvas?: { blocks?: Array<{ type?: string }> } } }> } | null | undefined
) {
  const sections = Array.isArray(layout?.sections) ? layout.sections : [];
  return sections.some((section) =>
    Array.isArray(section?.settings?.canvas?.blocks) &&
    section.settings.canvas.blocks.some((block) => block?.type === 'site-progress')
  );
}

export function shouldSuppressShellProgressForLayout(
  layout: { sections?: Array<{ type?: string; settings?: { canvas?: { presentation?: string; blocks?: Array<{ type?: string }> } } }> } | null | undefined
) {
  void layout;
  return true;
}
